import type { MsgKey } from '../i18n';
import type { Robot, UpdateRowState } from '../api/types';

/** PROTOCOL §29 — shared helpers for the Updates page and the robot header chip. */

export const STATE_KEYS: Record<UpdateRowState, MsgKey> = {
  done: 'upd.s.done',
  offline: 'upd.s.offline',
  manual: 'upd.s.manual',
  paused: 'upd.s.paused',
  waiting_canary: 'upd.s.waiting_canary',
  pending: 'upd.s.pending',
  downloading: 'upd.s.downloading',
  failed: 'upd.s.failed',
  unknown_sources: 'upd.s.unknown_sources',
  waiting_idle: 'upd.s.waiting_idle',
  snoozed: 'upd.s.snoozed',
  prompt: 'upd.s.prompt',
  installer: 'upd.s.installer',
};

export type Tone = 'green' | 'blue' | 'amber' | 'red' | 'grey';

export function rowTone(s: UpdateRowState): Tone {
  switch (s) {
    case 'done':
      return 'green';
    case 'downloading':
    case 'pending':
      return 'blue';
    case 'failed':
      return 'red';
    case 'offline':
    case 'paused':
    case 'waiting_canary':
      return 'grey';
    default:
      return 'amber';
  }
}

/** Filter tab of a row on the Updates page. */
export function rowGroup(s: UpdateRowState): 'idle' | 'tap' | 'problems' | 'offline' | 'other' {
  if (s === 'waiting_idle' || s === 'snoozed') return 'idle';
  if (s === 'prompt' || s === 'installer') return 'tap';
  if (s === 'failed' || s === 'unknown_sources' || s === 'manual') return 'problems';
  if (s === 'offline') return 'offline';
  return 'other';
}

export interface Chip {
  key: MsgKey;
  vars?: Record<string, string | number>;
  extra?: { key: MsgKey; vars: Record<string, string | number> };
  tone: Tone | 'plain';
}

/** The robot page header chip "App 0.2.4 → …" from the robot's live `status.update` (design "Обновления 2"). */
export function updateChip(robot: Robot): Chip | null {
  const v = robot.appVersion;
  if (!v) return null;
  const u = robot.status?.update;
  const plain: Chip = { key: 'upd.chip.version', vars: { v }, tone: 'plain' };
  if (!robot.online || !u || !u.versionName || u.state === 'idle') return plain;
  const pct = u.total ? Math.min(100, Math.round((u.bytes / u.total) * 100)) : 0;
  switch (u.state) {
    case 'downloading':
      return { key: 'upd.chip.downloading', vars: { pct }, tone: 'blue' };
    case 'failed':
      return { key: 'upd.chip.failed', tone: 'red' };
    case 'prompt':
    case 'installer':
      return { key: 'upd.chip.tap', tone: 'amber' };
    case 'ready':
      if (!u.unknownSources) return { key: 'upd.chip.unknownSources', tone: 'amber' };
      if (u.busy) return { key: 'upd.chip.waitingIdle', tone: 'amber' };
      return { ...plain, extra: { key: 'upd.chip.available', vars: { v: u.versionName } } };
    default:
      return plain;
  }
}
